import * as Sentry from "@sentry/vue";

const apiHost = import.meta.env.VITE_API_BACKEND;

/**
 * Common fetch options for API requests
 * @param {Object} headers - Request headers
 * @returns {Object} - Fetch options
 */
const getCommonFetchOptions = (headers = {}) => ({
  credentials: "same-origin",
  mode: "cors",
  headers: {
    "Content-Type": "application/json",
    ...headers
  }
});

/**
 * Fetches combined information for a scan
 * @param {string} scanId - The scan ID
 * @param {string} apiKey - The API key
 * @returns {Object} - The published STC config
 * @throws {Error} - If the fetch operation fails
 */
async function getScanCombinedInfos(scanId, apiKey) {
  try {
    const url = `https://${apiHost}/api/v2/consumer/scan/${scanId}/combined-info/`;

    const response = await fetch(
      url,
      getCommonFetchOptions({
        "X-ScanTrust-Consumer-Api-Key": apiKey
      })
    );

    if (!response.ok) {
      Sentry.captureException(new Error(`Failed to fetch campaign data: ${response.status}`));
      throw new Error(`Failed to fetch campaign data: ${response.status}`);
    }

    const { stc_config, campaign } = await response.json();
    return stc_config ? stc_config["published"] : campaign.options.stc_config["published"];
  } catch (error) {
    Sentry.captureException(new Error(`Error fetching campaign data: ${error.message}`));
    throw error;
  }
}

/**
 * Fetches STC config by key
 * @param {string} key - The STC config key
 * @returns {Object} - The config data
 * @throws {Error} - If the fetch operation fails
 */
async function getStcConfigByKey(key) {
  const url = `https://${apiHost}/api/v2/consumer/stc-config/${key}/`;
  try {
    const response = await fetch(url, getCommonFetchOptions());

    if (!response.ok) {
      Sentry.captureException(new Error(`Failed to fetch StcConfig: ${response.status}`));
      throw new Error(`Failed to fetch StcConfig: ${response.status}`);
    }

    const data = await response.json();
    return data.config;
  } catch (error) {
    console.error("Error fetching StcConfig:", error);
    Sentry.captureException(new Error(`Error fetching StcConfig: ${error}`));
    throw error;
  }
}

export { getScanCombinedInfos, getStcConfigByKey };
